import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import MatchingPositionCard from "../components/MatchingPositionCard";
import PositionModal from "../components/PositionCard/PositionModal";

export default function MatchingPage() {
  const navigate = useNavigate();
  // Read user object from localStorage
  const stored = localStorage.getItem("user");
  const userData = stored ? JSON.parse(stored) : {};
  const userId = userData.id;
  const token = userData.token;
  const [matches, setMatches] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showMatches, setShowMatches] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasCheckedForMatches, setHasCheckedForMatches] = useState(false);
  const [matchesRemaining, setMatchesRemaining] = useState(null);
  
  // Check for existing matches when component loads
  useEffect(() => {
    const checkForExistingMatches = async () => {
      if (!userId || !token || hasCheckedForMatches) return;

      try {
        setIsLoading(true);
        const raw = await loadMatchedPositions();
        
        // Store matches remaining
        if (raw.matchesRemaining !== undefined) {
          setMatchesRemaining(raw.matchesRemaining);
        }

        if (raw.matches && raw.matches.length > 0) {
          // Load existing matches and show them immediately
          const combined = await loadFullPositions(raw.matches);
          setMatches(combined);
          setShowMatches(true);
        }
      } catch (error) {
        console.error("Error checking for existing matches:", error);
      } finally {
        setIsLoading(false);
        setHasCheckedForMatches(true);
      }
    };

    checkForExistingMatches();
  }, [userId, token, hasCheckedForMatches]);

  // 1) Fetch the raw matchedPositions array
  const loadMatchedPositions = async () => {
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/matches`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data; // array of { position: ObjectId, final_score }
  };

  // 2) Given rawMatches, load full Position docs + stitch scores back in
  const loadFullPositions = async (rawMatches) => {
    const ids = rawMatches.map(m => m.position).filter(Boolean);
    if (!ids.length) return [];
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/positions/batch`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { ids: ids.join(",") },
      }
    );
    const positions = resp.data; // array of full position objects
    // map by _id for quick lookup
    const byId = positions.reduce((acc, p) => {
      acc[p._id] = p;
      return acc;
    }, {});
    return rawMatches
      .map(m => ({
        position: byId[m.position],
        final_score: m.final_score
      }))
      .filter((x) => x.position)
      .sort((a, b) => b.final_score - a.final_score);
  };

  // 3) Click handler: load or generate matches, then fetch full docs
  const handleShowMatches = async () => {
    setIsLoading(true);
    // A) load existing matchedPositions
    let raw = await loadMatchedPositions();
    
    // Update matches remaining
    if (raw.matchesRemaining !== undefined) {
      setMatchesRemaining(raw.matchesRemaining);
    }
    
    // B) if empty, trigger backend to compute them
    if (raw.matches.length === 0) {
      await axios.post(
        `${process.env.REACT_APP_FASTAPI_URL}/match-candidate`,
        null,
        {
          params: { user_id: userId, top_n: 3 },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      raw = await loadMatchedPositions();
      
      // Update matches remaining after request
      if (raw.matchesRemaining !== undefined) {
        setMatchesRemaining(raw.matchesRemaining);
      }
    }
    else if(raw.matchesRemaining > 0) {
      await axios.post(
        `${process.env.REACT_APP_FASTAPI_URL}/match-candidate`,
        raw.matches,
        {
          params: { user_id: userId, top_n: 3 },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      raw = await loadMatchedPositions();
      
      // Update matches remaining after request
      if (raw.matchesRemaining !== undefined) {
        setMatchesRemaining(raw.matchesRemaining);
      }
    }
    // C) load full position docs + scores
    const combined = await loadFullPositions(raw.matches);
    setMatches(combined);
    // D) animate in
    setTimeout(() => {
      setShowMatches(true);
      setIsLoading(false);
      setHasCheckedForMatches(true);
    }, 1000);
  };

  // Handle position view - opens the modal
  const handlePositionView = (position) => {
    setSelectedPosition(position);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setSelectedPosition(null);
  };

  return (
    <div className="px-6 py-8 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 text-center">
        Your Perfect Research Matches Await!
      </h1>

      {/* Important Notice about Matching Algorithm */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Important:</strong> The matching algorithm can only be run
              once per student as of now.
              <span className="block mt-1 text-blue-600">
                💡 In the future, there will be more frequent matching
                opportunities.
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Feedback Box - only show after user has seen matches */}
      {showMatches && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Share Your Experience:</strong> How accurate were your
                matches?
                <button
                  onClick={() => navigate("/feedback-form")}
                  className="ml-2 text-green-600 hover:text-green-800 underline font-medium"
                >
                  Tell us about your matching experience →
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
      
      {!isLoading && hasCheckedForMatches && (
        <div className="text-center mb-8">
          <button
            onClick={handleShowMatches}
            className="match-button relative text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-200"
          >
            🎯 Show Your Matches!
          </button>
          <p className="text-gray-600 mt-2">Discover your top 3 research position matches</p>
          {matchesRemaining !== null && (
            <p className="text-sm text-gray-600 mt-2">
              {matchesRemaining} {matchesRemaining === 1 ? 'match' : 'matches'} remaining
            </p>
          )}
        </div>
      )}

      {isLoading && (
        <div className="text-center mb-8">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <div className="text-xl font-semibold text-blue-600 animate-pulse">
            Finding your perfect matches...
          </div>
        </div>
      )}

      {showMatches && (
        <>
          <h2 className="text-xl font-semibold mb-6 text-center">🎉 Your Top Matched Positions!</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {matches.map((match, idx) => (
              <div
                key={match.position._id || idx}
                className="animate-fadeInUp"
                style={{
                  animationDelay: `${idx * 200}ms`,
                  animationFillMode: "both",
                }}
              >
                {/* Use the MatchingPositionCard component */}
                <MatchingPositionCard
                  match={match}
                  onView={() => handlePositionView(match.position)}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Position Detail Modal - same as in StudentDashboard */}
      <PositionModal
        position={selectedPosition}
        isOpen={!!selectedPosition}
        onClose={handleCloseModal}
      />
      {/* Spacer to push footer down */}
      <div style={{ height: '270px' }}></div>
      
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes pulse {
          0%,
          100% {
            box-shadow: 0 0 20px rgba(242, 175, 41, 0.4);
          }
          50% {
            box-shadow: 0 0 40px rgba(242, 175, 41, 0.8);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out;
        }
        .match-button {
          background: linear-gradient(
            135deg,
            #4a9eff 0%,
            #f2af29 50%,
            #0066cc 100%
          );
          border: none;
          position: relative;
          overflow: hidden;
          animation: pulse 2s ease-in-out infinite;
        }
        .match-button::before {
          content: "";
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          background: linear-gradient(
            45deg,
            transparent,
            rgba(242, 175, 41, 0.6),
            transparent
          );
          border-radius: 12px;
          animation: rotate 3s linear infinite;
          z-index: -1;
        }
        .match-button:hover {
          transform: scale(1.05);
          animation: pulse 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
